import { getAccessToken, getColumnChoices, makeBatchRequest, sendRequest } from './index.js'
import axios from 'axios'
import bus from '../../utils/EventBus.js'
import Event from '../../utils/EventNames.js'
import Cache from '../../cache/Cache.js'

import { getSiteConfig } from '../../community/siteProvider.js'

export const ResourceStatus = {
  DRAFT: 'DRAFT',
  APPROVED: 'APPROVED'
}

export const getFileOpenUrl = async (file) => {
  const { siteId, driveIds } = getSiteConfig()
  const webUrl = file?.driveItem?.webUrl ?? file?.webUrl

  const documentId = file?.driveItem?.id
  const driveId = file?.driveItem?.parentReference?.driveId

  const previewData = await getFileResourcePreview(documentId, siteId, driveId) //driveIds['Shared Documents'])
  
  const { getUrl: _getUrl } = previewData
  return _getUrl
}

// export const getFileViewUrl = async (resourceId) => {
//   const { siteId } = getSiteConfig()
//   const accessToken = await getAccessToken()
//   const baseUrl = `https://graph.microsoft.com/v1.0/sites/${siteId}/drive/items/${resourceId}/createLink`
//   const c = {
//     headers: {
//       Authorization: `Bearer ${accessToken}`,
//       "Content-Type": "application/json"
//     }
//   }
//   const payload = {
//     type: "view"
//   }
//   try {
//   const response = await axios.post(baseUrl, payload, c)
//   if (response.status >= 200 && response.status < 400) {
//     const {data} = response
//     return data.link?.webUrl ?? undefined
//   } else {
//     return undefined
//   }
//   } catch (error) {
//     throw new Error(error.response?.data?.error?.message ?? error.message)
//   }
  
// }

export const getResourceTagChoices = async () => {
  const { listIds } = getSiteConfig()
  return await getColumnChoices(listIds.documents, 'QSEPortalTagChoices')
}

export const getResourceLibraryCategories = async () => {
  const { listIds } = getSiteConfig()
  return await getColumnChoices(listIds.documents, 'QSEPortalCategoryChoices')
}

export const getResourceFilesByCategoryUrl = (category, batch = false) => {
  const { siteId, listIds } = getSiteConfig()
  const p = batch ? '' : 'https://graph.microsoft.com/v1.0'
  return `${p}/sites/${siteId}/lists/${listIds.documentLibrary}/items?$expand=DriveItem&$filter=(fields/QSEPortalResourceCategory+eq+'${encodeURIComponent(category)}'+or+fields/QSEPortalResourceCategory+eq+'category:${encodeURIComponent(category)}')+and+fields/ContentType+eq+'Document'+and+fields/Status+ne+'${ResourceStatus.DRAFT}'`
}

export const getResourceLibraryFilesByCategory = async (category) => {
  const accessToken = await getAccessToken()
  const url = getResourceFilesByCategoryUrl(category) //`https://graph.microsoft.com/v1.0/sites/${siteId}/lists/${listIds.documentLibrary}/items?$expand=DriveItem&$filter=(fields/QSEPortalResourceCategory+eq+'${encodeURIComponent(category)}'+or+fields/QSEPortalResourceCategory+eq+'category:${encodeURIComponent(category)}')+and+fields/ContentType+eq+'Document'+and+fields/Status+ne+'${ResourceStatus.DRAFT}'`
  const { data } = await axios.get(url, {
    headers: {
      Authorization: `Bearer ${accessToken}`
    }
  })
  const { value } = data
  return value
}

export const getResourceLibraryFilesByCategoryBatch = async (categoriesArr, _accessToken) => {
  const accessToken = _accessToken ?? await getAccessToken()
  const resourceRequests = (categoriesArr ?? []).map(category => {
    return {
      id: category,
      url: getResourceFilesByCategoryUrl(category, true),
      method: 'GET'
    }
  })

  const batchRequests = makeBatchRequest(resourceRequests)
  
  const results = await sendRequest(batchRequests, accessToken)
  const responses = results.responses ?? []
  const validResults = responses.reduce((p, r) => {
    if (r.status >= 200 && r.status < 300) {
      return [...p, {id: r.id, body: r.body}]
    }
    return p
  }, [])
  
  return validResults
}

export const getResourceLibraryFileById = async (rId) => {
  const { siteId, listIds } = getSiteConfig()
  const accessToken = await getAccessToken()

  const url = `https://graph.microsoft.com/v1.0/sites/${siteId}/lists/${listIds.documentLibrary}/items/${rId}?$expand=DriveItem`
  const { data } = await axios.get(url, {
    headers: {
      Authorization: `Bearer ${accessToken}`
    }
  })

  return data
}

const getResourceLibraryFileBatchUrl = (rId, idIsDriveItemId = false) => {
  const { siteId, listIds } = getSiteConfig()
  if (idIsDriveItemId) {
    return `/sites/${siteId}/drive/items/${rId}/listItem?$expand=DriveItem`
  }
  return `/sites/${siteId}/lists/${listIds.documentLibrary}/items/${rId}?$expand=DriveItem`
}

export const getResourceLibraryFiles = async (idArr, _accessToken = undefined, idsAreDriveIds = false) => {
  const accessToken = _accessToken ?? await getAccessToken()
  const resourceRequests = (idArr ?? []).map(id => {
    return {
      id,
      url: getResourceLibraryFileBatchUrl(id, idsAreDriveIds),
      method: 'GET'
    }
  })

  const batchRequests = makeBatchRequest(resourceRequests)
  const results = await sendRequest(batchRequests, accessToken)
  const responses = results.responses ?? []
  const validResults = responses.reduce((p, r) => {
    if (r.status >= 200 && r.status < 300) {
      return [...p, r.body]
    }
    return p
  }, [])

  return [...validResults]
}

export const getSiteSettings = async (refreshCache = false) => {
  const { settingsFilePath } = getSiteConfig()
  const cacheKey = 'site-settings'
  if (!refreshCache) {
    const cacheHit = Cache.getItem(cacheKey)
    if(cacheHit) return cacheHit
  }
  const file = await getResourceLibraryFileByPath(settingsFilePath)
  let result = {}
  if(typeof file === 'string') {
    result = JSON.parse(file)
  } else {
    result = file
  }
  Cache.setItem(cacheKey, result)
  return result
}

export const getResourceLibraryFileByPath = async (filePath) => {
  const { siteId, driveIds } = getSiteConfig()
  const accessToken = await getAccessToken();

  const url = `https://graph.microsoft.com/v1.0/sites/${siteId}/drives/${driveIds.documents}/root:/${encodeURIComponent(filePath)}`;
  
  const { data } = await axios.get(url, {
    headers: {
      Authorization: `Bearer ${accessToken}`
    }
  });

  const stuff = await axios.get(data['@microsoft.graph.downloadUrl'])
  return stuff.data;
};

export const uploadFileToPath = async (
  filePathRelative,
  fileContent,
  contentType = 'application/octet-stream'
) => {
  const { siteId, driveIds } = getSiteConfig()
  const accessToken = await getAccessToken();
  
  const url = `https://graph.microsoft.com/v1.0/sites/${siteId}/drives/${driveIds.documents}/root:/${filePathRelative}:/content`;
  
  const { data } = await axios.put(url, fileContent, {
    headers: {
      Authorization: `Bearer ${accessToken}`,
      'Content-Type': contentType
    }
  });

  return data; // Graph returns file metadata
};

export const getResourceLibraryFilesByTag = async (tags) => {
  const { urls } = getSiteConfig()
  const accessToken = await getAccessToken()

  const tagsSearchTerms = tags.includes(',') ? tags.split(',').map(t => `"${t}"`).join(' ') : `"${tags}"`
  const searchUrl = `https://graph.microsoft.com/v1.0/search/query`
  const searchBody = {
    "requests": [
      {
        "entityTypes": [
          "driveItem"
        ],
        "query": {
          "queryString": `${tagsSearchTerms} path: "${urls.baseSite}/Shared Documents" AND ParentLink:\"/Published\"`
        }
      }
    ]
  }
  const { data: searchData } = await axios.post(searchUrl, searchBody, {
    headers: {
      Authorization: `Bearer ${accessToken}`
    }
  })

  const searchValue = searchData.value[0]

  const idArr = (searchValue?.hitsContainers[0].hits ?? []).map(h => h.resource.listItem.id)
  return await getResourceLibraryFiles(idArr, accessToken)
}

export const getItemAnalytics = async (itemId, timeframe = 'allTime') => {
  const { siteId, listIds } = getSiteConfig()
  const accessToken = await getAccessToken()
  const url = `https://graph.microsoft.com/v1.0/sites/${siteId}/lists/${listIds.documentLibrary}/items/${itemId}/analytics/${timeframe}`
  const { data } = await axios.get(url, {
    headers: {
      Authorization: `Bearer ${accessToken}`
    }
  })
  const { value } = data
  return value
}

export const getItemAnalyticsBatch = async (itemIdList, timeframe = 'allTime') => {
  const { siteId, listIds } = getSiteConfig()
  const accessToken = await getAccessToken()
  const url = `https://graph.microsoft.com/v1.0/$batch`

  const requests = itemIdList.map(i => {
    return {
      id: i,
      method: 'GET',
      url: `/sites/${siteId}/lists/${listIds.documentLibrary}/items/${i}/analytics/${timeframe}`
    }
    //https://graph.microsoft.com/v1.0
  })

  const requestChunks = ((arr, chunkSize) => {
    const chunks = [];
    for (let i = 0; i < arr.length; i += chunkSize) {
      const chunk = arr.slice(i, i + chunkSize);
      chunks.push(chunk);
    }
    return chunks;
  })(requests, 20)

  let allResponses = []
  for (const chunk of requestChunks) {
    const { data } = await axios.post(url, { requests: chunk }, {
      headers: {
        Authorization: `Bearer ${accessToken}`,
        "Content-Type": 'application/json',
        "Accept": 'application/json'
      }
    })
    allResponses = [...allResponses, ...data.responses]
  }
  return allResponses
}

export const getResourceLibraryFilesInStatus = async (status) => {
  const { siteId, listIds } = getSiteConfig()
  const accessToken = await getAccessToken()
  const url = `https://graph.microsoft.com/v1.0/sites/${siteId}/lists/${listIds.documentLibrary}/items?$expand=DriveItem&$filter=fields/Status+eq+'${status}'+and+fields/ContentType+eq+'Document'`
  const { data } = await axios.get(url, {
    headers: {
      Authorization: `Bearer ${accessToken}`
    }
  })
  const { value } = data
  return value
}

export const publishResourceLibraryDraft = async (resourceId) => {
  const { siteId } = getSiteConfig()
  const accessToken = await getAccessToken()

  const gUrl = `https://graph.microsoft.com/v1.0/sites/${siteId}/drive/items/root:/Published`
  const gResponse = await axios.get(gUrl, {
    headers: {
      Authorization: `Bearer ${accessToken}`,
      "Content-Type": "application/json"
    }
  })

  const { data: publishedFolder } = gResponse

  const publishUrl = `https://graph.microsoft.com/v1.0/sites/${siteId}/drive/items/${resourceId}`
  let patchBody = {
    "Status": ResourceStatus.APPROVED
  }
  let patchResponse = await axios.patch(publishUrl + '/listItem/fields', patchBody, {
    headers: {
      Authorization: `Bearer ${accessToken}`,
      "Content-Type": "application/json"
    }
  })

  // Move the file to Published folder
  patchBody = {
    "parentReference": {
      "id": publishedFolder.id
    }
  }
  patchResponse = await axios.patch(publishUrl, patchBody, {
    headers: {
      Authorization: `Bearer ${accessToken}`,
      "Content-Type": "application/json"
    }
  })



}

export const getResourceLibraryFilesForIdentity = async ({ email, username }, folder) => {
  const { siteId } = getSiteConfig()
  const accessToken = await getAccessToken()
  const url = `https://graph.microsoft.com/v1.0/sites/${siteId}/drive/root:/${folder}:/children?$expand=listItem&?filter=fields/ContentType+eq+'Document'`
  const { data } = await axios.get(url, {
    headers: {
      Authorization: `Bearer ${accessToken}`
    }
  })
  const { value } = data

  //restructure to ListItem response form
  const list = value.reduce((p, d) => {
    if ([email, username].includes(d.createdBy.user.email)) {
      return [...p, { ...(structuredClone(d.listItem)), driveItem: structuredClone(d) }]
    }
    return p
  }, [])
  return list
}

export const checkFilenameConflict = async (folder, filename) => {
  const { siteId, driveIds } = getSiteConfig()
  const accessToken = await getAccessToken()
  let url = `https://graph.microsoft.com/v1.0/sites/${siteId}/drives/${driveIds.documents}/root:/${folder}:/children?$select=name`

  let conflict = false
  const c = {
    headers: {
      Authorization: `Bearer ${accessToken}`
    }
  }
  do {
    let searchResponse = await axios.get(url, c)
    const { value: files } = searchResponse.data
    for (const file of files) {
      if (file.name.toLowerCase() === filename.toLowerCase()) {
        conflict = true
        break
      }
    }
    url = searchResponse.data['@odata.nextLink']
  } while (url !== undefined)

  return conflict
}

const getResourceUploadUrl = async (fileProperties) => {
  const { siteId } = getSiteConfig()
  const accessToken = await getAccessToken()
  const url = `https://graph.microsoft.com/v1.0/sites/${siteId}/drive/items/root:/Submitted/${encodeURIComponent(fileProperties.fileName)}:/createUploadSession`
  const postBody = {
    "item": {
      "@microsoft.graph.conflictBehavior": "rename",
      "name": `${fileProperties.fileName}`
    }
  }
  const { status, data } = await axios.post(url, postBody, {
    headers: {
      Authorization: `Bearer ${accessToken}`
    }
  })

  const { uploadUrl, expirationDateTime } = data
  return { uploadUrl, expirationDateTime }
}

function getUploadRanges(fileLength, chunkSize = (30 * 320 * 1024)) {
  const ranges = []

  if (fileLength <= chunkSize) {
    ranges.push({ start: 0, end: fileLength - 1, size: fileLength })
  } else {
    let start = 0
    while (start < fileLength) {
      const end = Math.min(start + chunkSize - 1, fileLength - 1)
      const size = end - start + 1
      ranges.push({ start, end, size })
      start = end + 1
    }
  }

  return ranges
}

export const updateFileResourceProperties = async (resourceId, fileProperties, asDraft = true) => {
  const { siteId } = getSiteConfig()
  const accessToken = await getAccessToken()
  const baseUrl = `https://graph.microsoft.com/v1.0/sites/${siteId}/drive/items/${resourceId}`
  let patchBody = {
    "description": fileProperties.description,
    "title": fileProperties.title,
  }
  let patchResponse = await axios.patch(baseUrl, patchBody, {
    headers: {
      Authorization: `Bearer ${accessToken}`,
      "Content-Type": "application/json"
    }
  })

  patchBody = {
    "QSEPortalResourceCategory": fileProperties.category.includes('category:') ? fileProperties.category : `category:${fileProperties.category}`
  }
  if (asDraft) {
    patchBody["Status"] = ResourceStatus.DRAFT
  }
  const tags = fileProperties.tags ?? []
  if (tags.length > 0) {
    patchBody['QSEPortalTags'] = tags.map(t => {
      if (!t.includes('tag:')) return `tag:${t}`
      return t
    }).join(',')
  } else {
    patchBody['QSEPortalTags'] = null
  }
  patchResponse = await axios.patch(baseUrl + '/listItem/fields', patchBody, {
    headers: {
      Authorization: `Bearer ${accessToken}`,
      "Content-Type": "application/json"
    }
  })
  return patchResponse?.data
}

const uploadRangeChunk = async (chunkInfo) => {
  const {uploadUrl, size, start, end, blob, fileSize, attempt, expirationDateTime} = chunkInfo
  let response, uploadStatus
  try {
    response = await axios.put(uploadUrl, blob, {
      headers: {
        "Content-Length": size,
        "Content-Range": `bytes ${start}-${end}/${fileSize}`
      }
    })
    uploadStatus = 'success'
  } catch (error) {
    uploadStatus = 'error'
    console.error(error)
    const delay = Math.abs(Math.pow(1.5, attempt) * 1000); 
    bus.emit(Event.ALERT, {
      "text": `Error uploading, retrying in ${delay / 1000} seconds. Expiration: ${expirationDateTime}`,
      "style": "error",
      "duration": delay,
    })
    await new Promise(resolve => setTimeout(resolve, delay))
  }

  return {
    response,
    uploadStatus
  }
}

export const conductFileResourceUpload = async (fileProperties, targetFile, progressEvent = undefined, asDraft = true) => {
  let resourceId = undefined
  const ranges = getUploadRanges(fileProperties.fileSize)
  const conductSize = 3 + ranges.length
  let progress = 0
  const reportProgress = () => {
    if (progressEvent) {
      bus.emit(progressEvent, {
        completed: conductSize,
        progress: ++progress
      })
    }
  }

  try {
    reportProgress()
    const { uploadUrl, expirationDateTime } = await getResourceUploadUrl(fileProperties)
    const _expirationDateTime = new Date(expirationDateTime).getTime()

    reportProgress()
    let completedResponse
    for (const r of ranges) {
      const size = r.size
      const start = r.start
      const end = r.end
      const blob = targetFile.slice(start, end + 1)
      
      let attempt = 0
      let chunkUploadStatus
      do {
        attempt++
        const chunkInfo = {uploadUrl, size, start, end, blob, fileSize: fileProperties.fileSize, attempt, expirationDateTime}
        const { response, uploadStatus } = await uploadRangeChunk(chunkInfo)
        chunkUploadStatus = uploadStatus
        if (uploadStatus === 'success') {
          completedResponse = response
        }
      } while ( attempt < 11 && chunkUploadStatus === 'error' && new Date().getTime() < _expirationDateTime );

      // completedResponse = await axios.put(uploadUrl, blob, {
      //   headers: {
      //     "Content-Length": size,
      //     "Content-Range": `bytes ${start}-${end}/${fileProperties.fileSize}`
      //   }
      // })


      reportProgress()
    }
    const uploadResponseData = completedResponse?.data
    const { id: newResourceId } = uploadResponseData    
    const resourcePropertyUpdate = await updateFileResourceProperties(newResourceId, fileProperties, asDraft)
    resourceId = newResourceId
    reportProgress()
  } catch (error) {
    bus.emit(Event.ALERT, {
      "text": error.message,
      "style": "error",
      "duration": 10000,
    })
  }
  return resourceId
}

export const getFileResourcePreview = async (resourceId, _siteId, _driveId) => {
  const { siteId } = getSiteConfig()
  const accessToken = await getAccessToken()
  const url = _siteId && _driveId
    ? `https://graph.microsoft.com/v1.0/sites/${_siteId}/drives/${_driveId}/items/${resourceId}/preview`
    : `https://graph.microsoft.com/v1.0/sites/${siteId}/drive/items/${resourceId}/preview`
  const postBody = {}
  const { status, data } = await axios.post(url, postBody, {
    headers: {
      Authorization: `Bearer ${accessToken}`,
      "Content-Type": "application/json"
    }
  })
  return data
}

export const getFileDownloadUrl = async (resourceId, _siteId, _driveId) => {
  const { siteId } = getSiteConfig()
  const accessToken = await getAccessToken();
  
  const url = _siteId && _driveId
    ? `https://graph.microsoft.com/v1.0/sites/${_siteId}/drives/${_driveId}/items/${resourceId}`
    : `https://graph.microsoft.com/v1.0/sites/${siteId}/drive/items/${resourceId}`;

  const { data } = await axios.get(url, {
    headers: {
      Authorization: `Bearer ${accessToken}`
    }
  });

  // This is a pre-authenticated download URL that lasts only a short time
  return data['@microsoft.graph.downloadUrl'];
};

const deleteResource = async (resourceId, isPublished) => {
  const { siteId } = getSiteConfig()
  const accessToken = await getAccessToken()
  const baseUrl = `https://graph.microsoft.com/v1.0/sites/${siteId}/drive/items/${resourceId}`

  const c = {
    headers: {
      Authorization: `Bearer ${accessToken}`,
      "Content-Type": "application/json"
    }
  }

  if (isPublished) {
    await axios.delete(baseUrl, c)
  } else {
    await axios.post(baseUrl + '/permanentDelete', {}, c)
  }
}

export const deleteDraftResource = async (resourceId) => {
  await deleteResource(resourceId, false)
}

export const deletePublishedResource = async (resourceId) => {
  await deleteResource(resourceId, true)
}